import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ComponentPropsWithoutRef } from "react";

// Lightweight, dependency-free prose styling (no @tailwindcss/typography needed).
// Maps common Markdown elements to Tailwind classes for a clean reader view.
type MdProps<T extends keyof JSX.IntrinsicElements> = ComponentPropsWithoutRef<T>;

const components = {
  h1: (props: MdProps<"h1">) => (
    <h1 className="mt-6 mb-3 text-2xl font-bold text-slate-900" {...props} />
  ),
  h2: (props: MdProps<"h2">) => (
    <h2 className="mt-6 mb-2 text-xl font-semibold text-slate-900" {...props} />
  ),
  h3: (props: MdProps<"h3">) => (
    <h3 className="mt-5 mb-2 text-lg font-semibold text-slate-800" {...props} />
  ),
  p: (props: MdProps<"p">) => (
    <p className="my-3 leading-relaxed text-slate-700" {...props} />
  ),
  ul: (props: MdProps<"ul">) => (
    <ul className="my-3 ml-6 list-disc space-y-1 text-slate-700" {...props} />
  ),
  ol: (props: MdProps<"ol">) => (
    <ol className="my-3 ml-6 list-decimal space-y-1 text-slate-700" {...props} />
  ),
  li: (props: MdProps<"li">) => <li className="leading-relaxed" {...props} />,
  strong: (props: MdProps<"strong">) => (
    <strong className="font-semibold text-slate-900" {...props} />
  ),
  em: (props: MdProps<"em">) => <em className="italic" {...props} />,
  a: (props: MdProps<"a">) => (
    <a
      className="text-sky-700 underline underline-offset-2 hover:text-sky-900"
      {...props}
    />
  ),
  code: (props: MdProps<"code">) => (
    <code
      className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-sm text-slate-800"
      {...props}
    />
  ),
  pre: (props: MdProps<"pre">) => (
    <pre
      className="my-4 overflow-x-auto rounded-lg bg-slate-900 p-4 text-sm text-slate-100"
      {...props}
    />
  ),
  blockquote: (props: MdProps<"blockquote">) => (
    <blockquote
      className="my-4 border-l-4 border-slate-300 pl-4 italic text-slate-600"
      {...props}
    />
  ),
  hr: () => <hr className="my-6 border-slate-200" />,
  table: (props: MdProps<"table">) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  th: (props: MdProps<"th">) => (
    <th
      className="border border-slate-200 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-800"
      {...props}
    />
  ),
  td: (props: MdProps<"td">) => (
    <td className="border border-slate-200 px-3 py-2 text-slate-700" {...props} />
  ),
};

export default function Markdown({ children }: { children: string }) {
  return (
    <div className="text-slate-700">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
